import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { CASE_STUDIES } from "@/data/content";
import { Reveal } from "@/components/Reveal";

const Card = ({ cs, i }) => {
  const ref = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    el.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };

  return (
    <Reveal delay={(i % 2) * 120} className={cs.span}>
      <Link
        ref={ref}
        to={`/work/${cs.id}`}
        onMouseMove={onMove}
        data-testid={`case-study-card-${i}`}
        className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#111] transition-colors duration-500 hover:border-yellow-400/70"
      >
        <div
          className="pointer-events-none absolute inset-0 z-10 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          style={{
            background:
              "radial-gradient(400px circle at var(--mx) var(--my), rgba(250,204,21,0.12), transparent 60%)",
          }}
        />

        <div className="relative h-56 overflow-hidden sm:h-72">
          <img
            src={cs.image}
            alt={cs.client}
            className="h-full w-full object-cover grayscale transition-[transform,filter] duration-700 ease-out group-hover:scale-105 group-hover:grayscale-0"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-[#111]/20 to-transparent" />
          <span className="font-mono-accent absolute left-5 top-5 text-xs uppercase tracking-[0.25em] text-yellow-400">
            {cs.index} — {cs.category}
          </span>
          <div className="absolute bottom-5 left-5">
            <div className="font-display text-5xl leading-none text-white sm:text-6xl">
              {cs.result}
            </div>
            <div className="font-mono-accent mt-1 text-[10px] uppercase tracking-[0.2em] text-neutral-400">
              {cs.metric}
            </div>
          </div>
        </div>

        <div className="relative flex flex-1 flex-col p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <h3 className="text-xl font-semibold text-white sm:text-2xl">
              {cs.client}
            </h3>
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/15 text-neutral-400 transition-[transform,color,border-color] duration-300 group-hover:rotate-45 group-hover:border-yellow-400 group-hover:text-yellow-400">
              <ArrowUpRight size={16} />
            </span>
          </div>
          <p className="mt-2 text-base font-medium text-yellow-400">
            {cs.headline}
          </p>
          <p className="mt-4 text-sm leading-relaxed text-neutral-400">
            {cs.body}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {cs.tags.map((t) => (
              <span
                key={t}
                className="rounded-full border border-white/10 px-3 py-1 font-mono-accent text-[10px] uppercase tracking-[0.15em] text-neutral-400"
              >
                {t}
              </span>
            ))}
          </div>
          <span className="mt-6 inline-flex items-center gap-2 font-mono-accent text-[11px] uppercase tracking-[0.2em] text-neutral-500 transition-colors duration-300 group-hover:text-yellow-400">
            View case study
            <ArrowUpRight
              size={13}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </span>
        </div>
      </Link>
    </Reveal>
  );
};

export const CaseStudies = () => {
  return (
    <section
      id="work"
      data-testid="case-studies-section"
      className="relative bg-[#0a0a0a] py-24 sm:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="mb-16 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Reveal
              as="p"
              y={20}
              className="mb-4 font-mono-accent text-xs uppercase tracking-[0.3em] text-yellow-400"
            >
              / Selected Work
            </Reveal>
            <Reveal
              as="h2"
              delay={80}
              className="font-display text-5xl uppercase leading-[0.9] text-white sm:text-7xl"
            >
              Case studies,
              <br />
              <span className="text-stroke">not promises.</span>
            </Reveal>
          </div>
          <Reveal
            as="p"
            delay={200}
            className="max-w-xs text-sm leading-relaxed text-neutral-400"
          >
            Every engagement is measured in one currency: profit. Here's what
            that looks like in the wild.
          </Reveal>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {CASE_STUDIES.map((cs, i) => (
            <Card key={cs.id} cs={cs} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
